import axios, {
  AxiosResponse,
  AxiosResponseHeaders,
  InternalAxiosRequestConfig,
  RawAxiosResponseHeaders,
} from 'axios'

import { getApiUrl } from '../helpers/apiHelpers'
import { buildUrl } from '../helpers/helperFunctions'
import { currentPath } from '../helpers/routeHelpers'
import { ROUTES } from '../routes'
import authService from './authService'
import errorTypeService from './errorTypeService'
import sentryService from './sentryService'

const apiService = axios.create({
  baseURL: getApiUrl(),
  withCredentials: true,
})

function setRefererPathHeader(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (!config.headers) {
    return config
  }

  config.headers['X-Referer-Path'] = (
    window.location.pathname + window.location.search
  ).substr(1)

  return config
}

function handleRefreshExpiryHeader(
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders,
) {
  if (headers['x-refresh-token-expires-at']) {
    const expiryTime = parseInt(headers['x-refresh-token-expires-at'], 10)
    if (!isNaN(expiryTime)) {
      // If numeric set new expiry time - LogoutWarning.tsx will handle logout if it's expired
      return authService.dispatchSessionExtended(expiryTime)
    }
  }
  // If no refresh token expiry set we can assume the user isn't logged in anymore
  authService.dispatchSessionIsAuthenticated(false)
}

function handleErrors(error: unknown) {
  if (errorTypeService.isTokenError(error)) {
    authService.logout()
    return
  }

  if (errorTypeService.isExpiredUserContextError(error)) {
    console.log('Reload the page to refresh the user context.')
    window.location.reload()
    return
  }

  if (errorTypeService.isCloudflareChallengeResponse(error)) {
    window.location.assign(
      buildUrl(ROUTES.challenge, { redirectUri: currentPath() }),
    )
    return
  }

  if (axios.isAxiosError(error) && error.response?.headers) {
    // Handle session expiry update from API error responses
    handleRefreshExpiryHeader(error.response.headers)
  }

  sentryService.captureApiError(error)

  // Don't handle any other error types
  return Promise.reject(error)
}

function handleResponse(response: AxiosResponse) {
  const headers = response.headers

  if (headers['x-company-outstanding-payments']) {
    window.dispatchEvent(
      new CustomEvent<string>('outstandingPayments', {
        detail: headers['x-company-outstanding-payments'],
      }),
    )
  }

  // Handle session expiry from API responses
  handleRefreshExpiryHeader(headers)

  return response
}

apiService.interceptors.request.use(setRefererPathHeader)
apiService.interceptors.response.use(
  // Any status code within the 2xx range will trigger this function
  (response) => handleResponse(response),

  // Any status codes outside the 2xx range will trigger this function
  (error) => handleErrors(error),
)

export default apiService
